// ---------------------------------------------------------------------------
// YouAreYou (uru) — Passkey Registration Prompt
//
// Modal that appears after the user's first magic link login, prompting them
// to register a passkey (Face ID / Touch ID) for faster future sign-ins.
//
// Triggered by the `shouldPromptPasskey` flag in AuthContext. When true, this
// modal renders on top of whatever page the user is on (typically /dashboard).
//
// Flow:
//   1. "Set Up Passkey" screen — explains benefits, optional device name input
//   2. "Registering…" — calls passkeyRegisterBegin → startRegistration →
//      passkeyRegisterComplete
//   3. "Success" — confirmation with checkmark, auto-dismisses or user clicks
//   4. "Error" — retry or skip
//
// Usage (in App.tsx or AppLayout):
//   <PasskeyRegistrationPrompt />
//   (reads shouldPromptPasskey from useAuth — no props needed)
//
// API references (docs/api.md):
//   - POST /api/account/passkeys/register/begin → challenge + publicKey options
//   - navigator.credentials.create() via @simplewebauthn/browser startRegistration
//   - POST /api/account/passkeys/register/complete → PasskeyInfo
//
// Known considerations:
//   - WebAuthn passkeys are domain-bound: staging ≠ production ≠ localhost
//   - startRegistration will throw if WebAuthn is not supported or user cancels
//   - The friendly_name defaults to a generated device name but user can customize
//   - "Skip" dismisses the prompt for the session only (setShouldPromptPasskey(false))
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from "react";
import {
  startRegistration,
  browserSupportsWebAuthn,
  type RegistrationResponseJSON,
} from "@simplewebauthn/browser";
import { passkeyRegisterBegin, passkeyRegisterComplete } from "@/api/endpoints";
import { useAuth } from "@/hooks/useAuth";
import Icon from "@/components/ui/Icon";
import LogoSpinner from "@/components/ui/LogoSpinner";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type PromptStep =
  | "intro" // Show explanation + device name input
  | "registering" // WebAuthn ceremony in progress
  | "success" // Passkey registered successfully
  | "error"; // Registration failed

// ---------------------------------------------------------------------------
// Device name detection
// ---------------------------------------------------------------------------

function detectDeviceName(): string {
  const ua = navigator.userAgent;

  if (/iPhone/.test(ua)) return "Face ID on iPhone";
  if (/iPad/.test(ua)) return "Touch ID on iPad";
  if (/Macintosh/.test(ua) && "ontouchend" in document)
    return "Touch ID on Mac";
  if (/Macintosh/.test(ua)) return "Touch ID on Mac";
  if (/Android/.test(ua)) return "Passkey on Android";
  if (/Windows/.test(ua)) return "Windows Hello";
  if (/Linux/.test(ua)) return "Passkey on Linux";

  return "My Passkey";
}

// ---------------------------------------------------------------------------
// Inner Modal Component (mounts fresh when prompt is shown)
// ---------------------------------------------------------------------------

function PasskeyRegistrationPromptInner({ onClose }: { onClose: () => void }) {
  const [step, setStep] = useState<PromptStep>("intro");
  const [deviceName, setDeviceName] = useState(detectDeviceName);
  const [errorMessage, setErrorMessage] = useState("");

  const modalRef = useRef<HTMLDivElement>(null);
  const webAuthnSupported = browserSupportsWebAuthn();

  // ---------------------------------------------------------------------------
  // Body scroll lock
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Focus trap + Escape to close
  // ---------------------------------------------------------------------------

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        if (step === "registering") return; // Don't dismiss during ceremony
        onClose();
        return;
      }

      if (e.key !== "Tab") return;

      const modal = modalRef.current;
      if (!modal) return;

      const focusableElements = modal.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );

      if (focusableElements.length === 0) return;

      const firstEl = focusableElements[0];
      const lastEl = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        }
      } else {
        if (document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, step]);

  // ---------------------------------------------------------------------------
  // Auto-dismiss success after 3 seconds
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (step !== "success") return;

    const timer = setTimeout(() => {
      onClose();
    }, 3000);

    return () => clearTimeout(timer);
  }, [step, onClose]);

  // ---------------------------------------------------------------------------
  // Registration handler
  // ---------------------------------------------------------------------------

  const handleRegister = useCallback(async () => {
    setStep("registering");
    setErrorMessage("");

    try {
      // Step 1: Get challenge from server
      const beginData = await passkeyRegisterBegin();

      // Step 2: Call WebAuthn via @simplewebauthn/browser
      let regResponse: RegistrationResponseJSON;
      try {
        regResponse = await startRegistration({
          optionsJSON: beginData.publicKey,
        });
      } catch (err) {
        // User cancelled the WebAuthn prompt or browser doesn't support it
        const msg =
          err instanceof Error ? err.message : "Passkey creation cancelled";

        if (
          msg.includes("cancelled") ||
          msg.includes("canceled") ||
          msg.includes("abort") ||
          msg.includes("NotAllowedError")
        ) {
          // User intentionally cancelled — go back to intro, don't show error
          setStep("intro");
          return;
        }

        setErrorMessage(msg);
        setStep("error");
        return;
      }

      // Step 3: Send credential to server
      const friendlyName = deviceName.trim() || "My Passkey";

      await passkeyRegisterComplete({
        challenge_id: beginData.challenge_id,
        friendly_name: friendlyName,
        credential: {
          id: regResponse.id,
          rawId: regResponse.rawId,
          type: regResponse.type,
          response: {
            clientDataJSON: regResponse.response.clientDataJSON,
            attestationObject: regResponse.response.attestationObject,
          },
          transports: regResponse.response.transports as
            | AuthenticatorTransport[]
            | undefined,
        },
      });

      setStep("success");
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : "Failed to register passkey. Please try again.";
      setErrorMessage(msg);
      setStep("error");
    }
  }, [deviceName]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center px-4 pb-safe sm:items-center sm:pb-4"
      role="presentation"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="passkey-prompt-title"
        className="relative z-10 w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-2xl sm:rounded-2xl rounded-t-2xl"
      >
        {/* ---- Intro Step ---- */}
        {step === "intro" && (
          <div className="flex flex-col items-center p-6 text-center">
            {/* Icon */}
            <div className="mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-primary-50">
              <Icon
                name="passkey"
                className="text-4xl text-primary-500"
                label="Passkey"
              />
            </div>

            <h2
              id="passkey-prompt-title"
              className="text-xl font-bold text-text-primary"
            >
              Secure Your Account
            </h2>
            <p className="mt-2 text-sm text-text-secondary">
              Set up Face ID, Touch ID, or a security key for faster,
              password-free sign-in on this device.
            </p>

            {/* Benefits */}
            <div className="mt-5 w-full space-y-2.5">
              <div className="flex items-center gap-3 rounded-lg bg-gray-50 px-3 py-2.5 text-left">
                <Icon name="speed" className="shrink-0 text-xl text-verified" />
                <span className="text-xs text-text-secondary">
                  <strong className="text-text-primary">Instant sign-in</strong>{" "}
                  — no codes to wait for
                </span>
              </div>
              <div className="flex items-center gap-3 rounded-lg bg-gray-50 px-3 py-2.5 text-left">
                <Icon name="lock" className="shrink-0 text-xl text-verified" />
                <span className="text-xs text-text-secondary">
                  <strong className="text-text-primary">Phishing-proof</strong>{" "}
                  — bound to this domain only
                </span>
              </div>
              <div className="flex items-center gap-3 rounded-lg bg-gray-50 px-3 py-2.5 text-left">
                <Icon
                  name="devices"
                  className="shrink-0 text-xl text-verified"
                />
                <span className="text-xs text-text-secondary">
                  <strong className="text-text-primary">
                    Per-device security
                  </strong>{" "}
                  — stays on this device
                </span>
              </div>
            </div>

            {/* Device name input */}
            <div className="mt-5 w-full">
              <label
                htmlFor="passkey-device-name"
                className="mb-1 block text-left text-xs font-medium text-text-muted"
              >
                Device name
              </label>
              <input
                id="passkey-device-name"
                type="text"
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                placeholder="My Passkey"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none"
              />
            </div>

            {/* WebAuthn not supported warning */}
            {!webAuthnSupported && (
              <div className="mt-4 flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-left">
                <Icon
                  name="warning"
                  className="mt-0.5 shrink-0 text-lg text-warning"
                />
                <p className="text-xs text-amber-700">
                  This browser doesn&apos;t support passkeys. You can still use
                  SMS codes to sign in.
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="mt-6 flex w-full flex-col gap-2.5">
              <button
                onClick={handleRegister}
                disabled={!webAuthnSupported}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary-500 px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-primary-600 focus:ring-2 focus:ring-primary-500/20 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Icon name="fingerprint" className="text-xl" />
                Set Up Passkey
              </button>
              <button
                onClick={onClose}
                className="w-full py-2 text-sm font-medium text-text-muted transition-colors hover:text-text-secondary"
              >
                Skip for now
              </button>
            </div>
          </div>
        )}

        {/* ---- Registering Step ---- */}
        {step === "registering" && (
          <div className="flex flex-col items-center p-8 text-center">
            <div className="mb-5">
              <LogoSpinner size="md" />
            </div>
            <h2 className="text-lg font-bold text-text-primary">
              Follow the prompt
            </h2>
            <p className="mt-2 text-sm text-text-secondary">
              Use Face ID, Touch ID, or your security key when your browser
              prompts you.
            </p>
            <div className="mt-6 flex items-center gap-2 text-xs text-text-muted">
              <Icon name="info" className="text-base" />
              <span>
                This window will update automatically when you&apos;re done.
              </span>
            </div>
          </div>
        )}

        {/* ---- Success Step ---- */}
        {step === "success" && (
          <div className="flex flex-col items-center p-8 text-center">
            <div className="mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-green-50">
              <Icon
                name="check_circle"
                filled
                className="text-5xl text-verified"
                label="Passkey registered successfully"
              />
            </div>
            <h2 className="text-lg font-bold text-text-primary">
              Passkey Registered!
            </h2>
            <p className="mt-2 text-sm text-text-secondary">
              You can now sign in with{" "}
              <strong className="text-text-primary">
                {deviceName.trim() || "your passkey"}
              </strong>{" "}
              — no codes needed.
            </p>
            <button
              onClick={onClose}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-primary-500 px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-primary-600 focus:ring-2 focus:ring-primary-500/20 focus:outline-none"
            >
              Continue to Dashboard
              <Icon name="arrow_forward" className="text-lg" />
            </button>
            <p className="mt-3 text-xs text-text-muted">
              Closing automatically…
            </p>
          </div>
        )}

        {/* ---- Error Step ---- */}
        {step === "error" && (
          <div className="flex flex-col items-center p-6 text-center">
            <div className="mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-red-50">
              <Icon
                name="error"
                filled
                className="text-5xl text-error"
                label="Registration failed"
              />
            </div>
            <h2 className="text-lg font-bold text-text-primary">
              Registration Failed
            </h2>
            <p className="mt-2 text-sm text-text-secondary">{errorMessage}</p>

            {/* Troubleshooting tips */}
            <div className="mt-4 w-full rounded-lg border border-gray-200 bg-gray-50 p-3 text-left">
              <p className="mb-2 text-xs font-semibold text-text-primary">
                Troubleshooting:
              </p>
              <ul className="space-y-1 text-xs text-text-secondary">
                <li className="flex items-start gap-1.5">
                  <Icon
                    name="check"
                    className="mt-0.5 shrink-0 text-xs text-text-muted"
                  />
                  Make sure biometric auth is enabled in device settings
                </li>
                <li className="flex items-start gap-1.5">
                  <Icon
                    name="check"
                    className="mt-0.5 shrink-0 text-xs text-text-muted"
                  />
                  Try using the browser&apos;s built-in authenticator
                </li>
                <li className="flex items-start gap-1.5">
                  <Icon
                    name="check"
                    className="mt-0.5 shrink-0 text-xs text-text-muted"
                  />
                  If on a company device, check with your IT department
                </li>
              </ul>
            </div>

            <div className="mt-5 flex w-full flex-col gap-2.5">
              <button
                onClick={handleRegister}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary-500 px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-primary-600 focus:ring-2 focus:ring-primary-500/20 focus:outline-none"
              >
                <Icon name="refresh" className="text-lg" />
                Try Again
              </button>
              <button
                onClick={onClose}
                className="w-full py-2 text-sm font-medium text-text-muted transition-colors hover:text-text-secondary"
              >
                Skip for now
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Outer Component — reads auth context and conditionally renders
// ---------------------------------------------------------------------------

export default function PasskeyRegistrationPrompt() {
  const { shouldPromptPasskey, setShouldPromptPasskey } = useAuth();

  const handleClose = useCallback(() => {
    setShouldPromptPasskey(false);
  }, [setShouldPromptPasskey]);

  if (!shouldPromptPasskey) return null;

  return <PasskeyRegistrationPromptInner onClose={handleClose} />;
}
